import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';
import { AccountRefSchema } from '../../users/account-ref/account-ref';
import { BillingCurrencySchema } from '../currency/currency';
import { DirectPaymentMethodSchema } from '../direct-payment-method/direct-payment-method';
import { DirectPaymentOrderOriginSchema } from '../direct-payment-order-origin/direct-payment-order-origin';
import { DirectPaymentOrderStatusSchema } from '../direct-payment-order-status/direct-payment-order-status';
import { MoneyAmountSchema } from '../money/money';
import { ProductKeySchema } from '../product-key/product-key';
import { ProductKindSchema } from '../product-kind/product-kind';
import {
  ProductDurationDaysSchema,
  TierKeySchema,
} from '../subscription-product/subscription-product';

export const ORDER_NOTE_MAX_LENGTH = 500;
export const ORDER_REASON_MAX_LENGTH = 500;

/**
 * One staff-recorded payment, and everything a later reader needs to know what
 * was agreed (`03-payments-iap.md` §Data flow — Direct payments).
 *
 * ## Every commercial term is a SNAPSHOT
 *
 * `tier`, `durationDays` and the three amounts are copied onto the order when it
 * is entered, never joined back to the product or re-derived from the price
 * setting. `user_entitlements` snapshots `productKey`/`tier` for this reason and
 * `discount_grants` snapshots its whole terms for it; the same argument applies
 * with more force here, because this row is a record of **money that changed
 * hands**. Re-pricing a plan on Tuesday must not retroactively change what a
 * member was charged on Monday, and copying makes that structural: there is no
 * join through which the current price could reach a settled order.
 *
 * ## The three amounts are all kept, not two and a subtraction
 *
 * `listAmount - discountAmount = chargedAmount`, so one of them is arithmetically
 * redundant — and it is stored anyway. A discount that was applied is a fact a
 * member may query and an auditor may have to reconstruct, and deriving it later
 * from a price setting that has since moved would give the wrong answer with
 * total confidence.
 *
 * ## `grantIds` carries no foreign key
 *
 * The grants live in `courses` and billing may not read them (N6). What is kept
 * is the ids that were claimed, so a rejection knows what to give back and an
 * auditor can ask the other module what they were worth.
 */
export const DirectPaymentOrderSchema = z
  .object({
    id: UuidSchema,
    userId: UuidSchema,

    productKey: ProductKeySchema,
    /** Snapshot: a plan order grants an entitlement, a fee order settles a fee (D9a). */
    kind: ProductKindSchema,
    /** Snapshot: the tier the entitlement will carry; `null` on a fee. */
    tier: TierKeySchema.nullable(),
    /** Snapshot: how long the entitlement runs, in days, from confirmation; `null` on a fee. */
    durationDays: ProductDurationDaysSchema.nullable(),

    /** The gender-resolved price before any discount (`billing.directPrices`, U15). */
    listAmount: MoneyAmountSchema,
    /** What the member's earned grants took off. Zero when none applied. */
    discountAmount: MoneyAmountSchema,
    /** What the member actually owes — `listAmount - discountAmount`. */
    chargedAmount: MoneyAmountSchema,
    currency: BillingCurrencySchema,

    /** The grants claimed at entry, and released again if the order is rejected. */
    grantIds: z.array(UuidSchema),

    /**
     * Which door the order came through (F3). A `member` order was opened by
     * the member from the phone with its receipt already bound, and
     * `createdByUserId` is then the member's own id, not a staff account.
     */
    origin: DirectPaymentOrderOriginSchema,
    method: DirectPaymentMethodSchema,
    note: z.string().max(ORDER_NOTE_MAX_LENGTH).nullable(),
    /**
     * The receipt, in the private bucket (N12). Nullable, and it stays nullable:
     * an order may legitimately be recorded before its evidence arrives, and a
     * purged receipt must not take the order with it.
     */
    evidenceMediaId: UuidSchema.nullable(),

    status: DirectPaymentOrderStatusSchema,
    /**
     * The entitlement this order granted. `null` until it is confirmed, and it
     * is what the refund path revokes — kept on the row so a refund never has to
     * guess which of a member's entitlements this order paid for.
     */
    entitlementId: UuidSchema.nullable(),
    /** The fee this order settled — a fee order's counterpart to `entitlementId`. */
    feePaymentId: UuidSchema.nullable(),
    /** Why it was rejected, or why it was refunded. One field: only one can happen. */
    decisionReason: z.string().max(ORDER_REASON_MAX_LENGTH).nullable(),

    /** When the terms were fixed and the member's grants were claimed. */
    submittedAt: z.iso.datetime().nullable(),
    confirmedAt: z.iso.datetime().nullable(),
    rejectedAt: z.iso.datetime().nullable(),
    refundedAt: z.iso.datetime().nullable(),

    createdByUserId: UuidSchema.nullable(),
    /** Who confirmed, rejected or refunded it. `null` while it is still open. */
    decidedByUserId: UuidSchema.nullable(),

    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
  })
  .strict();

export type DirectPaymentOrder = z.infer<typeof DirectPaymentOrderSchema>;

/**
 * An orders-queue row: the order, plus the member who owes it.
 *
 * A **separate** schema rather than a `member` field on the base, and the reason
 * is the leak P22b4 caught: `MemberDiscountGrantSchema` is built from its base by
 * `.omit()`, so a staff account ref added to the base flowed straight into the
 * member's own list. P23c builds the member's view of their orders from
 * {@link DirectPaymentOrderSchema}; keeping the staff lookup out here means that
 * derivation cannot inherit one.
 *
 * `null` is reachable and ordinary — a hard-deleted account (P19d) whose order
 * survives as the financial record it is.
 */
export const DirectPaymentOrderDetailSchema = DirectPaymentOrderSchema.extend({
  member: AccountRefSchema.nullable(),
}).strict();

export type DirectPaymentOrderDetail = z.infer<typeof DirectPaymentOrderDetailSchema>;
